import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, Alert } from "../ui-bits";
import { CostBadge } from "../ui-bits";
import api, { apiErr, thumbnailCostSeconds } from "../../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Loader2, Upload, Download, Sparkles, Check, ArrowRight } from "lucide-react";
import { useUploads } from "../../context/UploadContext";

export default function TabThumbnail({ project, refreshProject, setTab }) {
  const { t } = useTranslation();
  const { startThumbnail } = useUploads();
  const [thumbUrl, setThumbUrl] = useState(project?.custom_thumbnail?.url || "");
  const [thumbBusy, setThumbBusy] = useState(false);
  const [genCount, setGenCount] = useState(1);
  const [genBusy, setGenBusy] = useState(false);
  const [variants, setVariants] = useState(project?.ai_thumbnails || []);
  const [costSeconds, setCostSeconds] = useState(thumbnailCostSeconds(1));
  // Tras 2 fallos seguidos asumimos que es un bloqueo de contenido de la IA (no un error
  // puntual de red/cuota) y dejamos de ofrecer reintentar para no gastar más segundos del
  // plan en algo que probablemente vuelva a fallar igual.
  const [failCount, setFailCount] = useState(0);
  const aiBlocked = failCount >= 2;
  const fileRef = useRef();
  const hasThumb = !!thumbUrl;

  // Coste real desde el backend (espejo exacto del markup y coste de imagen del Admin).
  useEffect(() => {
    let cancel = false;
    api.post("/cost/estimate", { action: "thumbnail", count: genCount })
      .then(({ data }) => { if (!cancel && data?.seconds != null) setCostSeconds(data.seconds); })
      .catch(() => { if (!cancel) setCostSeconds(thumbnailCostSeconds(genCount)); });
    return () => { cancel = true; };
  }, [genCount]);

  const uploadThumb = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error(t("dl.thumbTooBig")); return; }
    setThumbBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/projects/${project.id}/thumbnail`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setThumbUrl(data.url); refreshProject?.();
      toast.success(t("dl.thumbUploaded"));
    } catch (e) { toast.error(apiErr(e)); } finally { setThumbBusy(false); }
  };
  const removeThumb = async () => {
    try { await api.delete(`/projects/${project.id}/thumbnail`); setThumbUrl(""); refreshProject?.(); toast.success(t("dl.thumbRemoved")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const generateThumbs = async () => {
    setGenBusy(true);
    try {
      const data = await startThumbnail(project.id, project.name, genCount);
      setVariants(data.thumbnails || []); refreshProject?.();
      setFailCount(0);
      toast.success(t("dl.aiThumbDone"));
    } catch (e) {
      toast.error(apiErr(e));
      setFailCount((c) => c + 1);
    } finally { setGenBusy(false); }
  };
  const pickVariant = async (v) => {
    try {
      const { data } = await api.post(`/projects/${project.id}/thumbnail/select`, { url: v.url, path: v.path });
      setThumbUrl(data.url); refreshProject?.();
      toast.success(t("dl.aiThumbSelected"));
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="p-6" data-testid="thumbnail-section">
      <div className="flex items-center gap-2 mb-1">
        <ImageIcon className="text-violet-600" size={26} />
        <h3 className="text-xl font-bold text-slate-900">{t("dl.thumbSectionTitle")}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-5">{t("dl.thumbStepIntro")}</p>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-48 aspect-video rounded-lg border-2 border-slate-200 bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
          {hasThumb ? <img src={thumbUrl} alt={t("dl.thumbAlt")} className="w-full h-full object-cover" data-testid="yt-thumb-preview" /> : <ImageIcon size={30} className="text-slate-300" />}
        </div>
        <div className="flex-1 min-w-[180px]">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png" className="hidden" data-testid="yt-thumb-input" onChange={(e) => uploadThumb(e.target.files?.[0])} />
          <button data-testid="yt-thumb-upload" onClick={() => fileRef.current?.click()} disabled={thumbBusy} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-white border-2 border-slate-300 hover:border-slate-400 font-bold text-slate-700 disabled:opacity-60">
            {thumbBusy ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />} {hasThumb ? t("dl.changeThumb") : t("dl.uploadThumb")}
          </button>
          {hasThumb && <a href={thumbUrl} download target="_blank" rel="noreferrer" data-testid="thumb-download" className="ml-2 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-800"><Download size={15} /> {t("dl.downloadThumb")}</a>}
          {hasThumb && <button data-testid="yt-thumb-remove" onClick={removeThumb} className="ml-2 text-sm font-semibold text-slate-500 hover:text-red-600">{t("dl.removeThumb")}</button>}
          <p className="text-xs text-slate-500 mt-1.5">{t("dl.thumbHint")}</p>
        </div>
      </div>

      <div className="mt-5 rounded-lg border-2 border-violet-200 bg-violet-50/50 p-4" data-testid="ai-thumb-box">
        <div className="flex items-start gap-2">
          <Sparkles size={18} className="text-violet-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-bold text-slate-900">{t("dl.aiThumbTitle")}</h4>
            <p className="text-xs text-slate-500 mt-0.5">{t("dl.aiThumbDesc")}</p>
          </div>
        </div>
        {aiBlocked ? (
          <div className="mt-3">
            <Alert type="warning" testid="ai-thumb-blocked">{t("dl.aiThumbBlocked")}</Alert>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <span className="text-sm text-slate-600">{t("dl.aiThumbVariants")}:</span>
            {[1, 2, 3].map((n) => (
              <button key={n} type="button" data-testid={`ai-thumb-count-${n}`} onClick={() => setGenCount(n)}
                className={`w-9 h-9 rounded-lg border-2 text-sm font-semibold transition-colors ${genCount === n ? "border-violet-600 bg-violet-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>{n}</button>
            ))}
            <button data-testid="ai-thumb-generate" onClick={generateThumbs} disabled={genBusy}
              className="ml-auto inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-violet-600 hover:bg-violet-700 font-bold text-white disabled:opacity-60">
              {genBusy ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
              {genBusy ? t("dl.aiThumbGenerating") : (variants.length ? t("dl.aiThumbGenerateMore") : t("dl.aiThumbGenerate"))}
              {!genBusy && <CostBadge seconds={costSeconds} tip={t("dl.aiThumbCostTip")} />}
            </button>
          </div>
        )}
        {variants.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3" data-testid="ai-thumb-variants">
            {variants.map((v, i) => {
              const sel = thumbUrl === v.url;
              return (
                <div key={v.path || i} className={`rounded-lg overflow-hidden border-2 ${sel ? "border-violet-600" : "border-slate-200"}`}>
                  <img src={v.url} alt={`variant-${i + 1}`} className="w-full aspect-video object-cover" />
                  <div className="flex">
                    <button data-testid={`ai-thumb-use-${i}`} onClick={() => pickVariant(v)} disabled={sel}
                      className={`flex-1 py-1.5 text-xs font-semibold ${sel ? "bg-violet-600 text-white" : "bg-white text-violet-700 hover:bg-violet-50"}`}>
                      {sel ? <span className="inline-flex items-center gap-1"><Check size={13} /> {t("dl.aiThumbInUse")}</span> : t("dl.aiThumbUse")}
                    </button>
                    <a href={v.url} download target="_blank" rel="noreferrer" data-testid={`ai-thumb-download-${i}`} title={t("dl.downloadThumb")}
                      className="px-2.5 py-1.5 bg-white text-slate-500 hover:text-blue-600 border-l border-slate-200"><Download size={14} /></a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 flex justify-end">
        <button data-testid="thumb-go-publish" onClick={() => setTab("descarga")} className="inline-flex items-center justify-center gap-2 min-h-[48px] px-4 sm:px-5 text-base rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700">
          {t("dl.thumbGoPublish")} <ArrowRight size={18} />
        </button>
      </div>
    </Card>
  );
}
